import { useState, useEffect, useRef } from 'react';
import { SITE_DEFAULTS } from '../data/content';

const ServicesSection = ({ services }) => {
  const items = services ?? SITE_DEFAULTS.services;
  const containerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking && containerRef.current) {
        window.requestAnimationFrame(() => {
          const rect = containerRef.current.getBoundingClientRect();
          const containerH = containerRef.current.offsetHeight;
          const viewH = window.innerHeight;
          const scrolled = -rect.top;
          const scrollable = containerH - viewH;

          if (scrolled > 0 && scrolled < scrollable) {
            const fraction = Math.max(0, Math.min(scrolled / scrollable, 1));
            setScrollProgress(fraction);
            const idx = Math.min(Math.floor(fraction * items.length), items.length - 1);
            setActive(idx);
          }

          if (rect.top < viewH * 0.8) setEntered(true);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [items.length]);

  if (!items || items.length === 0) return null;

  return (
    <section
      className="bcsvc"
      id="services"
      ref={containerRef}
      style={{ height: `${items.length * 100 + 100}vh` }}
    >
      <div className="bcsvc__sticky">
        <div className="bcsvc__border-top" />

        <div className="bcsvc__layout">
          {/* Text Column */}
          <div className="bcsvc__text">
            <div
              className="bcsvc__header"
              style={{
                opacity: entered ? 1 : 0,
                transform: entered ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <span className="bcsvc__eyebrow">— What We Offer</span>
              <h2 className="bcsvc__title">
                The<br />
                <span className="bcsvc__title-accent">Services.</span>
              </h2>
            </div>

            <ul className="bcsvc__list">
              {items.map((s, i) => (
                <li
                  key={s.id || i}
                  className={`bcsvc__item ${active === i ? 'bcsvc__item--active' : ''}`}
                  style={{
                    opacity: entered ? 1 : 0,
                    transform: entered ? 'translateY(0)' : 'translateY(32px)',
                    transition: `opacity 0.6s ease ${0.1 + i * 0.08}s, transform 0.6s cubic-bezier(0.16,1,0.3,1) ${0.1 + i * 0.08}s`,
                  }}
                >
                  <span className="bcsvc__item-number">0{i + 1}</span>
                  <span className="bcsvc__item-title">{s.title}</span>
                </li>
              ))}
            </ul>

            <div
              className="bcsvc__desc-wrap"
              style={{
                opacity: entered ? 1 : 0,
                transition: 'opacity 0.6s ease 0.4s',
              }}
            >
              {items.map((s, i) => {
                const block = s.content?.[0];
                return (
                  <div
                    key={i}
                    className={`bcsvc__desc ${active === i ? 'bcsvc__desc--active' : ''}`}
                  >
                    {block?.intro && <p>{block.intro}</p>}
                    {block?.items && (
                      <ul className="bcsvc__desc-list">
                        {block.items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {block?.outro && <p>{block.outro}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Image Column */}
          <div
            className="bcsvc__media"
            style={{
              opacity: entered ? 1 : 0,
              transition: 'opacity 0.8s ease 0.2s',
            }}
          >
            {items.map((s, i) => (
              <div
                key={i}
                className={`bcsvc__img-layer ${active === i ? 'bcsvc__img-layer--active' : ''}`}
              >
                {s.image ? (
                  <img src={s.image} alt={s.title} loading="lazy" decoding="async" />
                ) : (
                  <div className="bcsvc__img-placeholder">
                    <span className="bcsvc__img-placeholder-number">0{i + 1}</span>
                    <span className="bcsvc__img-placeholder-label">{s.title}</span>
                  </div>
                )}
              </div>
            ))}

            <div className="bcsvc__media-corner bcsvc__media-corner--tl" />
            <div className="bcsvc__media-corner bcsvc__media-corner--br" />
          </div>
        </div>

        {/* Mobile Images */}
        <div className="bcsvc__media-mobile">
          {items.map((s, i) => (
            <div
              key={i}
              className={`bcsvc__img-layer ${active === i ? 'bcsvc__img-layer--active' : ''}`}
            >
              {s.image ? (
                <img src={s.image} alt={s.title} />
              ) : (
                <div className="bcsvc__img-placeholder">
                  <span className="bcsvc__img-placeholder-number">0{i + 1}</span>
                  <span className="bcsvc__img-placeholder-label">{s.title}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Scroll Progress */}
        <div className="bcsvc__progress">
          <div
            className="bcsvc__progress-fill"
            style={{ transform: `scaleY(${scrollProgress})` }}
          />
        </div>

        {/* Scroll Hint */}
        <div
          className={`bcsvc__scroll-hint ${
            scrollProgress > 0.05 ? 'bcsvc__scroll-hint--hidden' : ''
          }`}
          style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.6s ease 0.6s' }}
        >
          <div className="bcsvc__scroll-line" />
          <span>Scroll</span>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;